/*
  Mega menu v6 — hover to open, click to navigate, drag-proof.

  v5: every top-level link opens a panel on hover, not just the ones with a
  submenu. Links that have children keep their own panel ("into the mix").
  Links without children share the single auto-built card grid rendered as
  #MegaMenu-Shared, so the "shop" submenu can be dropped from the Shopify
  navigation without losing the card grid — and "shop all" becomes a plain
  one-tap link on mobile.

  ROOT CAUSE FIX for the "frozen page" bug:
  A press-and-drag on nav text or a card image starts a native browser
  drag/selection. Native drags suppress all mouse/pointer events tab-wide.
  The v1-v3 close timer then mutated the DOM (collapsing the <details>)
  mid-drag, wedging Chrome's drag session and leaving the tab with mouse
  events permanently suppressed ("frozen in whatever state at unclick").

  v4 defenses:
  1. dragstart is cancelled for the ENTIRE inline nav (capture phase) —
     covers summaries, links, card images, and selected text alike.
  2. All images in the nav get draggable=false (Firefox coverage).
  3. The close timer is drag-aware: if a native drag is active anywhere,
     closing is deferred until the drag ends. No DOM mutation mid-drag.
  4. Recovery nets kept from v2: outside-click, Escape, window blur.
*/

(function () {
  const HOVER_MQ = window.matchMedia('(hover: hover) and (pointer: fine)');
  const DESKTOP_MQ = window.matchMedia('(min-width: 990px)');
  const CLOSE_DELAY_MS = 150;

  const PANEL_SELECTOR = '.header__inline-menu details.mega-menu';
  const ACTIVE_CARD = 'mega-menu__card--active';

  const hoverMode = () => HOVER_MQ.matches && DESKTOP_MQ.matches;

  // --- Drag session tracking (document-wide) ---
  let dragActive = false;
  let pendingClosers = [];

  function onDragSettled() {
    dragActive = false;
    // Run any close that was deferred during the drag
    pendingClosers.forEach((fn) => fn());
    pendingClosers = [];
  }

  document.addEventListener('dragstart', () => { dragActive = true; }, true);
  document.addEventListener('dragend', onDragSettled, true);
  document.addEventListener('drop', onDragSettled, true);

  function closeMenu(details) {
    if (dragActive) {
      // NEVER mutate the DOM mid-drag — defer until the drag settles.
      pendingClosers.push(() => closeMenu(details));
      return;
    }
    details.removeAttribute('open');
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    details
      .querySelectorAll('.' + ACTIVE_CARD)
      .forEach((card) => card.classList.remove(ACTIVE_CARD));
  }

  function closeAll(except) {
    document.querySelectorAll(PANEL_SELECTOR + '[open]').forEach((details) => {
      if (details !== except) closeMenu(details);
    });
  }

  // Bind `trigger` so that hovering it opens `details` and leaving it closes
  // that panel after a short grace period. Several triggers may share a panel.
  const closeTimers = new WeakMap();

  function hoverToOpen(trigger, details) {
    const summary = details.querySelector('summary');

    trigger.addEventListener('pointerenter', () => {
      if (!hoverMode()) return;
      clearTimeout(closeTimers.get(details));
      closeAll(details);
      details.setAttribute('open', '');
      if (summary) summary.setAttribute('aria-expanded', 'true');
    });

    trigger.addEventListener('pointerleave', () => {
      if (!hoverMode()) return;
      clearTimeout(closeTimers.get(details));
      closeTimers.set(
        details,
        setTimeout(() => closeMenu(details), CLOSE_DELAY_MS)
      );
    });
  }

  function initMegaMenuHover() {
    const nav = document.querySelector('.header__inline-menu');
    if (!nav) return;

    // 1. No drag may ever begin from the nav — text, links, or images.
    nav.addEventListener('dragstart', (event) => event.preventDefault(), true);

    // 2. Belt-and-suspenders for Firefox: mark nav images non-draggable.
    nav.querySelectorAll('img').forEach((img) => (img.draggable = false));

    nav.querySelectorAll('header-menu > details').forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;

      const url = summary.dataset.url;

      hoverToOpen(details, details);

      // Click navigates instead of toggling — real mouse clicks only.
      if (url && url !== '#') {
        summary.addEventListener('click', (event) => {
          if (!hoverMode()) return;        // touch devices: stock toggle
          if (event.detail === 0) return;  // keyboard: stock toggle
          if (event.metaKey || event.ctrlKey || event.shiftKey) return; // modifier clicks: stock toggle
          event.preventDefault();
          window.location.href = url;
        });
      }
    });

    // --- Shared panel: links without a submenu all open the same card grid ---
    const shared = nav.querySelector('details.mega-menu--shared');
    if (shared) {
      // Hovering the panel itself keeps it open while the pointer travels
      // from the nav row down into the cards.
      hoverToOpen(shared, shared);

      const cards = Array.from(shared.querySelectorAll('[data-mega-card]'));

      nav.querySelectorAll('a[data-mega-shared]').forEach((link) => {
        // Nav word and card are matched by link URL, so the pairing survives
        // any reordering or renaming of the menu.
        const card = cards.find((c) => c.dataset.megaCard === link.dataset.megaShared);

        if (!card) {
          // A top-level link with no card of its own (a page, say). Hovering it
          // should dismiss whatever is open rather than show an unrelated grid.
          link.addEventListener('pointerenter', () => {
            if (hoverMode()) closeAll();
          });
          return;
        }

        hoverToOpen(link, shared);

        // Hovering the nav word lights up its card exactly as hovering the
        // card itself does.
        link.addEventListener('pointerenter', () => {
          if (!hoverMode()) return;
          cards.forEach((c) => c.classList.remove(ACTIVE_CARD));
          card.classList.add(ACTIVE_CARD);
        });

        link.addEventListener('pointerleave', () => {
          card.classList.remove(ACTIVE_CARD);
        });
      });
    }

    // --- Recovery nets ---
    document.addEventListener('pointerdown', (event) => {
      const insideOpenMenu = event.target.closest(PANEL_SELECTOR + '[open]');
      if (!insideOpenMenu) closeAll();
    });

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') closeAll();
    });

    window.addEventListener('blur', () => {
      // A window blur can coincide with an OS-level drag; settle first.
      onDragSettled();
      closeAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMegaMenuHover);
  } else {
    initMegaMenuHover();
  }
})();
